import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import useModalHistory from '../../hooks/useModalHistory';

const AdvancedSearchModal = ({ onClose }) => {
  const navigate = useNavigate();
  const { closeModal } = useModalHistory(true, onClose, 'advanced-search');
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    strength: [],
    origin: [],
    wrapper: [],
    size: [],
    priceRange: [],
    flavorNotes: []
  });

  const strengthOptions = ['Mild', 'Medium', 'Medium-Full', 'Full'];
  const originOptions = ['Cuba', 'Nicaragua', 'Dominican Republic', 'Honduras'];
  const wrapperOptions = ['Habano', 'Maduro', 'Connecticut', 'Corojo'];
  const sizeOptions = ['Robusto', 'Toro', 'Churchill', 'Corona', 'Gordo'];
  const priceRangeOptions = ['Under $5', '$5-$10', '$10-$15', '$15-$20', '$20+'];
  const flavorNoteOptions = [
    'Leather', 'Cedar', 'Pepper', 'Cocoa', 'Coffee', 
    'Earth', 'Cream', 'Nuts', 'Spice', 'Wood',
    'Chocolate', 'Honey', 'Vanilla', 'Citrus', 'Floral'
  ];

  const toggleFilter = (category, value) => {
    setFilters(prev => ({
      ...prev,
      [category]: prev[category].includes(value)
        ? prev[category].filter(v => v !== value)
        : [...prev[category], value]
    }));
  };

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (searchQuery) params.set('search', searchQuery);
    if (filters.strength.length) params.set('strength', filters.strength.join(','));
    if (filters.origin.length) params.set('origin', filters.origin.join(','));
    if (filters.wrapper.length) params.set('wrapper', filters.wrapper.join(','));
    if (filters.size.length) params.set('size', filters.size.join(','));
    if (filters.priceRange.length) params.set('price', filters.priceRange.join(','));
    if (filters.flavorNotes.length) params.set('flavor', filters.flavorNotes.join(','));
    
    onClose();
    navigate(`/?${params.toString()}`);
  };

  const FilterChip = ({ label, selected, onClick }) => (
    <button
      onClick={onClick}
      className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
        selected 
          ? 'bg-amber-600 text-white' 
          : 'bg-white/5 text-white/70 border border-white/10 hover:bg-white/10'
      }`}
    >
      {label}
    </button>
  );

  const FilterSection = ({ title, options, category }) => (
    <div className="mb-6">
      <h3 className="text-white font-bold mb-3">{title}</h3>
      <div className="flex flex-wrap gap-2">
        {options.map(option => (
          <FilterChip
            key={option}
            label={option}
            selected={filters[category].includes(option)}
            onClick={() => toggleFilter(category, option)}
          />
        ))}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-[#0B0B0C] z-50 overflow-y-auto">
      <div className="min-h-screen">
        <div className="flex items-center justify-between p-4 border-b border-white/10 sticky top-0 bg-[#0B0B0C] z-10">
          <button data-testid="close-advanced-search-button" onClick={closeModal}>
            <X className="text-white/70 hover:text-white" size={24} />
          </button>
          <h2 className="text-xl font-bold text-white">Advanced Search</h2>
          <div className="w-6"></div>
        </div>

        <div className="p-4 pb-24">
          <div className="mb-6">
            <h3 className="text-white font-bold mb-3">Search</h3>
            <Input
              placeholder="Cigar name or brand"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
            />
          </div>

          <FilterSection title="Flavor Notes" options={flavorNoteOptions} category="flavorNotes" />
          <FilterSection title="Strength" options={strengthOptions} category="strength" />
          <FilterSection title="Origin" options={originOptions} category="origin" />
          <FilterSection title="Wrapper" options={wrapperOptions} category="wrapper" />
          <FilterSection title="Size" options={sizeOptions} category="size" />
          <FilterSection title="Price Range" options={priceRangeOptions} category="priceRange" />

          <Button 
            onClick={handleSearch}
            className="w-full bg-amber-600 hover:bg-amber-700 text-white font-bold py-6 rounded-full mt-4"
          >
            Search
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdvancedSearchModal;
